import { inject } from "vue";
import { OfferData } from "@/types/OfferData";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import { RealtedOfferItem } from "@/types/offers/RelatedOfferItem";
import { PropertyOffers as DbPropertyOffer } from "acquaint-api/models/PropertyOffers";
import { OfferStatus } from "@/enums/property-offers/OfferStatus";

export function usePropertyOfferHelper() {
  const apiClient = inject<AcquaintApiClient>("AcquaintApiClient")!;
  const acquaintNextApiClient = inject<AcquaintApiNextClient>(
    "AcquaintApiNextClient",
  )!;

  return {
    editOffer: async (offerId: number, editableOffer: OfferData) => {
      try {
        return await apiClient.httpPut<OfferData>(
          `database/propertyoffers/${offerId}`,
          editableOffer,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },
    createOffer: async (editableOffer: OfferData) => {
      try {
        return await apiClient.httpPost<OfferData>(
          "database/propertyoffers",
          editableOffer,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },
    fetchOfferData: async (offerId: number) => {
      try {
        return await apiClient.httpGet<OfferData>(
          `database/propertyoffers/${offerId}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchDefaultData: async () => {
      try {
        return await apiClient.httpGet<OfferData>(
          "database/propertyoffers/defaultmodel",
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    loadRelatedOffers: async (contactId: number, propertyId: number) => {
      try {
        return await acquaintNextApiClient.httpPost<RealtedOfferItem[]>(
          "propertyoffers/LoadRelatedOffers",
          { contactId, propertyId },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return [];
      }
    },

    deletePropertyoffer: async (id: number) => {
      try {
        await apiClient.httpDeleteVoid(`database/propertyoffers/${id}`);
        return true;
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return false;
      }
    },

    getOfferStatus: async (id: number) => {
      try {
        const offer = await apiClient.httpGet<DbPropertyOffer>(
          `database/propertyoffers/${id}`,
        );
        return offer?.status ?? OfferStatus.Pending;
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return OfferStatus.Pending;
      }
    },

    lockOffer: async (id: number, isLock: boolean) => {
      try {
        await apiClient.httpPostVoid(`database/propertyoffers/${id}/lock`, {
          lock: isLock,
          extraInfo: "",
        });
        return true;
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return false;
      }
    },
  };
}
